import { Log, send } from '@openticket/lib-log';
import { ShopWidgetWidgetError } from './errors/ShopWidgetWidgetError';
import { hasGoogleScripts } from './hasGoogleScripts';

interface GALinker {
    decorate: (url: string) => string;
}

interface UrlDecorator {
    decorate(url: string): Promise<string | null>;
}

type WindowWithGA = {
    ga: {
        (): void;
        getAll: () => unknown[];
    };
    gaplugins: {
        Linker: {
            new(tracker: unknown): unknown;
        };
    };
}

const MAX_TIME_TO_POLL = 3000;

export async function analyticsUrlDecorator(baseUrl: string): Promise<string> {
    try {
        const maxTime = Date.now() + MAX_TIME_TO_POLL;

        // Extraction from GA4 should only be started once after the measurementId is extracted.
        // It therefore needs to be prepared and persisted.
        const ga4Decorator = useGA4Decorator(window, maxTime);

        do {
            const tryMinimum = new Promise((resolve: (value: void) => void): void => {
                window.setTimeout(resolve, 100);
            });

            const gtmUrl = decorateUrlWithGTM(baseUrl);
            if (gtmUrl) {
                return gtmUrl;
            }

            const ga4Url = await ga4Decorator.decorate(baseUrl);
            if (ga4Url) {
                return ga4Url;
            }

            const uaUrl = decorateUrlWithUA(baseUrl);
            if (uaUrl) {
                return uaUrl;
            }

            await tryMinimum;
        } while (hasGoogleScripts() && Date.now() <= maxTime);
    } catch (e) {
        send(
            new ShopWidgetWidgetError({
                slug: 'shop.error.shop_widget.ot_shop_widget.gtag.not_set',
                message: 'Failed to set analytics session',
            }),
            Log.Error,
        );
    }

    return baseUrl;
}

function useGA4Decorator(w: Window, maxDoneTimeMs: number): UrlDecorator {
    const state: {
        promise: Promise<string | null> | null;
    } = {
        promise: null,
    };

    async function decorate(url: string): Promise<string | null> {
        if (state.promise) {
            // Once gtag is fully loaded, it will sequentially pick up every item in the dataLayer and process it.
            // Therefore, it is not necessary to re-request the values in a loop, by persisting the original promise.
            return state.promise;
        }

        if (!isWindowWithDataLayer(w)) {
            return null;
        }

        const measurementId = getGA4MeasurementId(w);
        if (!measurementId) {
            return null;
        }

        state.promise = decorateUrlWithGtag(w, url, measurementId, maxDoneTimeMs);
        let timeoutId: number;
        return Promise.race([
            state.promise.then((result) => {
                clearTimeout(timeoutId);
                return result;
            }),
            new Promise((resolve: (value: null) => void): void => {
                timeoutId = window.setTimeout(() => resolve(null), 100);
            }),
        ]);
    }

    return { decorate };
}

function decorateUrlWithGTM(url: string): string | null {
    if (!isWindowWithOtGTM(window)) {
        return null;
    }

    return buildDecoratedUrl(url, window.OtGTMCid, window.OtGTMSid);
}

function decorateUrlWithUA(url: string): string | null {
    if (!isWindowWithUA(window)) {
        return null;
    }

    const [ firstTracker ] = window.ga.getAll();

    if (!firstTracker) {
        return null;
    }

    const linker = new window.gaplugins.Linker(firstTracker);

    if (!isGaLinker(linker)) {
        return null;
    }

    return linker.decorate(url);
}

async function decorateUrlWithGtag(
    w: Window & { dataLayer: unknown[]; },
    url: string,
    measurementId: string,
    maxDoneTimeMs: number,
): Promise<string | null> {
    // The timeout is clamped to a minimum of 100ms to prevent immediate resolution.
    // This 100ms addition will also give the scheduler a little grace time to prioritize values over timeouts.
    const timeoutMs = 100 + Math.max(0, maxDoneTimeMs - Date.now());

    const [ clientId, sessionId ] = await Promise.all([
        gtagPromisify(w, timeoutMs, 'get', measurementId, 'client_id'),
        gtagPromisify(w, timeoutMs, 'get', measurementId, 'session_id'),
    ]);

    if (!clientId || !sessionId) {
        return null;
    }

    return buildDecoratedUrl(url, clientId, sessionId);
}

// HELPERS

function buildDecoratedUrl(url: string, gacId: string, gasId: string): string {
    const strippedSessionId = extractSessionId(gasId);
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.set('gacId', gacId);
    parsed.searchParams.set('gasId', strippedSessionId);
    return parsed.toString();
}

function extractSessionId(gasId: string): string {
    const separatorIndex = gasId.lastIndexOf(':');
    return separatorIndex === -1 ? gasId : gasId.slice(separatorIndex + 1);
}

function gtagPromisify(w: Window & { dataLayer: unknown[]; }, timeoutMs: number, ...args: unknown[]): Promise<string | null> {
    return new Promise((resolve: (value: string | null) => void): void => {
        let timeoutId: number;

        w.dataLayer.push([ ...args, (value: unknown): void => {
            clearTimeout(timeoutId);
            resolve(value && typeof value === 'string' ? value : null);
        } ]);

        timeoutId = window.setTimeout(() => resolve(null), timeoutMs);
    });
}

function getGA4MeasurementId(w: Window & { dataLayer: unknown[]; }): string | null {
    for (const entry of w.dataLayer) {
        if (!Array.isArray(entry)) {
            continue;
        }

        const [ command, measurementId ] = entry as unknown[];

        if (typeof command === 'string'
            && command.toLowerCase() === 'config'
            && typeof measurementId === 'string'
            && measurementId.toUpperCase().startsWith('G-')
        ) {
            return measurementId;
        }
    }

    return null;
}

// GUARDS

function isWindowWithDataLayer(w: Window): w is Window & { dataLayer: unknown[]; } {
    return 'dataLayer' in w && Array.isArray(w.dataLayer);
}

function isWindowWithOtGTM(w: Window): w is Window & { OtGTMCid: string, OtGTMSid: string } {
    const typedWindow = w as Window & { OtGTMCid?: string; OtGTMSid?: string };
    return typeof typedWindow.OtGTMCid === 'string' && !!typedWindow.OtGTMCid
        && typeof typedWindow.OtGTMSid === 'string' && !!typedWindow.OtGTMSid;
}

function isWindowWithUA(w: Window): w is Window & WindowWithGA {
    return typeof w.ga === 'function'
        && 'getAll' in w.ga
        && typeof w.ga.getAll === 'function'
        && !!w.gaplugins
        && typeof w.gaplugins === 'object'
        && 'Linker' in w.gaplugins
        && typeof w.gaplugins.Linker === 'function';
}

function isGaLinker(linker: unknown): linker is GALinker {
    return !!linker
        && typeof linker === 'object'
        && typeof (linker as Record<string, unknown>).decorate === 'function';
}
